import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { Heart, Loader2, Trash2, MapPin, Eye, Bell, BellOff, TrendingDown, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Favorites = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [priceAlertsEnabled, setPriceAlertsEnabled] = useState(true);
  const [updatingAlert, setUpdatingAlert] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/favorites', message: 'Please login to view your favorites' } });
      return;
    }
    fetchFavorites();
    fetchStats();
  }, [navigate]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/favorites/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFavorites(data.favorites || []);
        setPriceAlertsEnabled(data.price_alerts_enabled);
      } else {
        setError(data.error || 'Failed to load favorites');
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/favorites/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const removeFavorite = async (listingId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/favorites/${listingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFavorites(favorites.filter(fav => fav.listing_id !== listingId));
        toast({
          title: 'Removed',
          description: 'Item removed from favorites'
        });
      }
    } catch (err) {
      console.error('Error removing favorite:', err);
      toast({
        title: 'Error',
        description: 'Failed to remove from favorites',
        variant: 'destructive'
      });
    }
  };

  const togglePriceAlert = async (listingId, currentEnabled) => {
    setUpdatingAlert(prev => ({ ...prev, [listingId]: true }));
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/favorites/${listingId}/price-alert`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: !currentEnabled })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFavorites(favorites.map(fav => 
          fav.listing_id === listingId 
            ? { ...fav, price_alert_enabled: data.alert_enabled }
            : fav
        ));
        toast({
          title: 'Updated',
          description: data.message
        });
      }
    } catch (err) {
      console.error('Error toggling alert:', err);
      toast({
        title: 'Error',
        description: 'Failed to update price alert',
        variant: 'destructive'
      });
    } finally {
      setUpdatingAlert(prev => ({ ...prev, [listingId]: false }));
    }
  };

  const formatPrice = (price) => {
    return formatCurrency(price || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-3">
              <Heart className="h-10 w-10 fill-white" />
              <div>
                <h1 className="text-4xl font-bold">My Favorites</h1>
                <p className="text-red-100">Items you've saved for later</p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-80 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="h-10 w-10 fill-white" />
              <div>
                <h1 className="text-4xl font-bold">My Favorites</h1>
                <p className="text-red-100">Items you've saved for later</p>
              </div>
            </div>
            {stats && (
              <div className="text-right">
                <p className="text-2xl font-bold">{stats.total_favorites}/{stats.max_allowed}</p>
                <p className="text-red-100 text-sm">{stats.with_price_alerts} with price alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Price Alerts Banner */}
        {priceAlertsEnabled && favorites.some(f => f.price_drop) && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-green-700">
              <TrendingDown className="h-5 w-5" />
              <span className="font-semibold">Price Drops!</span>
              <span className="text-green-600">Some of your saved items have dropped in price</span>
            </div>
          </div>
        )}

        {favorites.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">No favorites yet</h2>
              <p className="text-gray-500 mb-6">
                Start saving items you like by clicking the heart icon on listings
              </p>
              <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-600 to-purple-600">
                Browse Listings
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">{favorites.length} item{favorites.length !== 1 ? 's' : ''} saved</p>
              {priceAlertsEnabled && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Bell className="h-4 w-4" />
                  Price alerts available
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((item) => (
                <Card key={item.id || item.listing_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    {/* Image */}
                    <div 
                      className="h-48 bg-gray-100 cursor-pointer"
                      onClick={() => navigate(`/listing/${item.listing_id}`)}
                    >
                      {item.listing?.images && item.listing.images.length > 0 ? (
                        <img
                          src={item.listing.images[0].startsWith('http') ? item.listing.images[0] : `${API_URL}${item.listing.images[0]}`}
                          alt={item.listing?.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>
                    
                    {/* Remove button */}
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2 rounded-full h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(item.listing_id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>

                    {/* Price Drop Badge */}
                    {item.price_drop && (
                      <Badge className="absolute top-2 left-2 bg-green-500 text-white border-0">
                        <TrendingDown className="h-3 w-3 mr-1" />
                        {item.price_drop.percentage}% off
                      </Badge>
                    )}

                    {/* Featured badge */}
                    {item.listing?.featured && !item.price_drop && (
                      <Badge className="absolute top-2 left-2 bg-yellow-500 text-white border-0">
                        Featured
                      </Badge>
                    )}

                    {/* Listing Deleted Warning */}
                    {item.listing_deleted && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge variant="destructive" className="text-lg py-2">
                          Listing Removed
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 
                      className="font-semibold text-lg mb-2 line-clamp-2 cursor-pointer hover:text-blue-600"
                      onClick={() => navigate(`/listing/${item.listing_id}`)}
                    >
                      {item.listing?.title || 'Listing unavailable'}
                    </h3>
                    
                    {/* Price with drop indicator */}
                    <div className="mb-3">
                      {item.price_drop ? (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-green-600">
                            {formatPrice(item.price_drop.current_price)}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            {formatPrice(item.price_drop.original_price)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-2xl font-bold text-blue-600">
                          {formatPrice(item.listing?.price || item.current_price)}
                        </p>
                      )}
                      {item.price_drop && (
                        <p className="text-sm text-green-600 font-medium">
                          You save {formatPrice(item.price_drop.savings)}!
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="truncate max-w-[120px]">{item.listing?.location || 'N/A'}</span>
                      </div>
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>{item.listing?.views || 0}</span>
                      </div>
                    </div>

                    {/* Price Alert Toggle */}
                    {priceAlertsEnabled && !item.listing_deleted && (
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2 text-sm">
                          {item.price_alert_enabled ? (
                            <Bell className="h-4 w-4 text-orange-500" />
                          ) : (
                            <BellOff className="h-4 w-4 text-gray-400" />
                          )}
                          <span className={item.price_alert_enabled ? 'text-orange-600' : 'text-gray-500'}>
                            Price Alert
                          </span>
                        </div>
                        <Switch
                          checked={item.price_alert_enabled || false}
                          onCheckedChange={() => togglePriceAlert(item.listing_id, item.price_alert_enabled)}
                          disabled={updatingAlert[item.listing_id]}
                        />
                      </div>
                    )}

                    {/* Alert Threshold Info */}
                    {item.price_alert_enabled && (
                      <p className="text-xs text-gray-400 mt-1">
                        Alert when price drops by {item.alert_threshold || 10}%
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;
